import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAppStore } from "@/app/store";
import { useForecasts } from "@/hooks/useForecasts";
import { OnboardingFlow } from "@/features/onboarding/OnboardingFlow";
import { TabBar } from "@/components/ui/TabBar";
import { Skeleton } from "@/components/ui/Skeleton";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { LogoMark } from "@/components/brand/Logo";

const DashboardScreen = lazy(async () => ({
  default: (await import("@/screens/DashboardScreen")).DashboardScreen
}));
const PropertiesScreen = lazy(async () => ({
  default: (await import("@/screens/PropertiesScreen")).PropertiesScreen
}));
const ScenariosScreen = lazy(async () => ({
  default: (await import("@/screens/ScenariosScreen")).ScenariosScreen
}));
const InsightsScreen = lazy(async () => ({
  default: (await import("@/screens/InsightsScreen")).InsightsScreen
}));
const SettingsScreen = lazy(async () => ({
  default: (await import("@/screens/SettingsScreen")).SettingsScreen
}));

export const App = () => {
  const {
    state,
    activeTab,
    selectedPropertyId,
    initialized,
    busy,
    initialize,
    setActiveTab,
    setTheme,
    markOnboardingComplete,
    loadDemo,
    addProperty,
    updateProperty,
    removeProperty,
    selectProperty,
    updateAssumptions,
    addScenario,
    importState,
    applyStatementImport,
    cloudConfigured,
    authIdentity,
    authAssuranceLevel,
    authBusy,
    cloudBusy,
    cloudStatusMessage,
    isPro,
    proStatusMessage,
    signUp,
    signIn,
    signOut,
    resendVerificationEmail,
    syncToCloud,
    restoreFromCloud,
    syncLatestStorePurchase,
    restoreStorePurchases
  } = useAppStore((store) => store);

  useEffect(() => {
    if (!initialized) {
      void initialize();
    }
  }, [initialize, initialized]);

  useEffect(() => {
    document.documentElement.classList.toggle(
      "dark",
      state.settings.theme === "dark"
    );
  }, [state.settings.theme]);

  const activeScreen = useMemo(() => {
    switch (activeTab) {
      case "dashboard":
        return "dashboard";
      case "properties":
        return "properties";
      case "scenarios":
        return "scenarios";
      case "insights":
        return "insights";
      case "settings":
        return "settings";
      default:
        return "dashboard";
    }
  }, [activeTab]);

  const { baseline } = useForecasts(null);
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");

  useEffect(() => {
    setAccountPanelOpen(false);
  }, [activeTab]);

  const accountTriggerLabel = authIdentity?.email
    ? authIdentity.email
    : "Account";
  const canSubmitAuth =
    accountEmail.trim().length > 0 && accountPassword.length > 0;
  const accountPanelMode = !cloudConfigured
    ? "not_configured"
    : authIdentity
      ? "signed_in"
      : "signed_out";

  if (!initialized || busy) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!state.settings.onboardingCompleted) {
    return (
      <OnboardingFlow
        onLoadDemo={loadDemo}
        onCompleteWithoutDemo={markOnboardingComplete}
        onCreateProperty={addProperty}
      />
    );
  }

  return (
    <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 pb-28 pt-4">
      <header className="mb-4">
        <div className="relative flex items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <LogoMark size={22} />
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-soft">
                Investment Property Mate
              </p>
            </div>
            <h1 className="text-2xl font-semibold">{state.portfolio.name}</h1>
            <p className="text-[11px] uppercase tracking-[0.14em] text-app-soft">
              Australian Made • Brisbane, QLD
            </p>
            {cloudConfigured && !authIdentity ? (
              <p className="text-xs text-app-soft">
                Saved on this device. Sign in to back up and use your portfolio
                on another device.
              </p>
            ) : null}
          </div>

          <div className="relative shrink-0">
            <Button
              variant="secondary"
              className="max-w-[12rem] truncate px-3 py-2"
              aria-haspopup="dialog"
              aria-expanded={accountPanelOpen}
              onClick={() => setAccountPanelOpen((current) => !current)}
              data-testid="account-menu-button"
            >
              {accountTriggerLabel}
            </Button>

            <AnimatePresence>
              {accountPanelOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 top-12 z-20 w-[min(92vw,22rem)] space-y-3 rounded-2xl border border-app bg-app-surface p-3 shadow-premium"
                >
                  {accountPanelMode === "not_configured" ? (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">
                        Online backup not set up
                      </p>
                      <p className="text-xs text-app-soft">
                        Set `VITE_CLOUD_API_URL` to enable sign in and cross-device
                        backup.
                      </p>
                    </div>
                  ) : null}

                  {accountPanelMode === "signed_out" ? (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">
                        Account
                      </p>
                      <p className="text-xs text-app-soft">
                        Sign in to back up and restore your portfolio across
                        devices.
                      </p>
                      <input
                        className="w-full rounded-lg border border-app bg-app-surface px-3 py-2 text-sm"
                        type="email"
                        value={accountEmail}
                        onChange={(event) => setAccountEmail(event.target.value)}
                        placeholder="Email address"
                      />
                      <input
                        className="w-full rounded-lg border border-app bg-app-surface px-3 py-2 text-sm"
                        type="password"
                        value={accountPassword}
                        onChange={(event) =>
                          setAccountPassword(event.target.value)
                        }
                        placeholder="Password (12+ chars)"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          disabled={!canSubmitAuth || authBusy}
                          onClick={async () => {
                            await signUp(
                              accountEmail.trim(),
                              accountPassword
                            );
                          }}
                        >
                          Create account
                        </Button>
                        <Button
                          disabled={!canSubmitAuth || authBusy}
                          onClick={async () => {
                            await signIn(
                              accountEmail.trim(),
                              accountPassword
                            );
                            setAccountPassword("");
                            setAccountPanelOpen(false);
                          }}
                        >
                          Sign in
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={authBusy || accountEmail.trim().length === 0}
                          onClick={async () => {
                            await resendVerificationEmail(accountEmail.trim());
                          }}
                        >
                          Resend email
                        </Button>
                      </div>
                      <p className="text-[11px] text-app-soft">
                        Tip: after creating an account, check your email to
                        verify before your first backup.
                      </p>
                    </div>
                  ) : null}

                  {accountPanelMode === "signed_in" && authIdentity ? (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold">
                        Signed in as {authIdentity.email ?? authIdentity.id}
                      </p>
                      <p className="text-xs text-app-soft">
                        Security level: {authAssuranceLevel.toUpperCase()}
                      </p>

                      {!isPro ? (
                        <p className="text-xs text-app-soft">
                          Free plan is active. Start or restore your mobile subscription,
                          then sync it from Settings.
                        </p>
                      ) : null}

                      <div className="flex flex-wrap gap-2">
                        <Button
                          variant="secondary"
                          disabled={cloudBusy}
                          onClick={syncToCloud}
                        >
                          Back up now
                        </Button>
                        <Button
                          disabled={cloudBusy}
                          onClick={restoreFromCloud}
                        >
                          Restore
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={cloudBusy}
                          onClick={syncLatestStorePurchase}
                        >
                          Sync purchase
                        </Button>
                        <Button
                          disabled={cloudBusy}
                          onClick={restoreStorePurchases}
                        >
                          Restore purchases
                        </Button>
                        <Button
                          variant="danger"
                          disabled={authBusy}
                          onClick={async () => {
                            await signOut();
                            setAccountPanelOpen(false);
                          }}
                        >
                          Sign out
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  {cloudStatusMessage ? (
                    <p className="text-xs text-app-soft">{cloudStatusMessage}</p>
                  ) : null}

                  {proStatusMessage ? (
                    <p className="text-xs text-app-soft">{proStatusMessage}</p>
                  ) : null}

                  {accountPanelMode === "signed_in" || accountPanelMode === "not_configured" ? (
                    <Button
                      variant="ghost"
                      className="w-full justify-start px-2 py-1.5 text-xs"
                      onClick={() => {
                        setActiveTab("settings");
                        setAccountPanelOpen(false);
                      }}
                    >
                      Account settings
                    </Button>
                  ) : null}
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </header>

      {cloudStatusMessage ? (
        <Card className="mb-4 flex items-center justify-between gap-3 border-app bg-app-surface-muted">
          <p className="text-xs text-app-soft">{cloudStatusMessage}</p>
          {!authIdentity ? (
            <Button
              variant="secondary"
              className="px-3 py-1.5 text-xs"
              onClick={() => setActiveTab("settings")}
            >
              Open Account
            </Button>
          ) : null}
        </Card>
      ) : null}

      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.section
            key={activeScreen}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            <Suspense fallback={<Skeleton className="h-64 w-full" />}>
              {activeScreen === "dashboard" ? (
                <DashboardScreen
                  forecast={baseline}
                  properties={state.portfolio.properties}
                />
              ) : null}

              {activeScreen === "properties" ? (
                <PropertiesScreen
                  properties={state.portfolio.properties}
                  selectedPropertyId={selectedPropertyId}
                  onSelect={selectProperty}
                  onAddProperty={addProperty}
                  onUpdateProperty={updateProperty}
                  onDeleteProperty={removeProperty}
                  forecast={baseline}
                  isPro={isPro}
                  proStatusMessage={proStatusMessage}
                  onOpenUpgrade={() => setActiveTab("settings")}
                />
              ) : null}

              {activeScreen === "scenarios" ? (
                <ScenariosScreen
                  scenarios={state.scenarios}
                  onSaveScenario={addScenario}
                />
              ) : null}

              {activeScreen === "insights" ? (
                <InsightsScreen forecast={baseline} />
              ) : null}

              {activeScreen === "settings" ? (
                <SettingsScreen
                  state={state}
                  onSetTheme={setTheme}
                  onSetForecastYears={updateAssumptions}
                  onImportState={importState}
                  onApplyStatementImport={applyStatementImport}
                  cloudConfigured={cloudConfigured}
                  authIdentity={authIdentity}
                  authAssuranceLevel={authAssuranceLevel}
                  authBusy={authBusy}
                  cloudBusy={cloudBusy}
                  cloudStatusMessage={cloudStatusMessage}
                  isPro={isPro}
                  proStatusMessage={proStatusMessage}
                  onSignUp={signUp}
                  onResendVerificationEmail={resendVerificationEmail}
                  onSignIn={signIn}
                  onSignOut={signOut}
                  onSyncToCloud={syncToCloud}
                  onRestoreFromCloud={restoreFromCloud}
                  onSyncLatestStorePurchase={syncLatestStorePurchase}
                  onRestoreStorePurchases={restoreStorePurchases}
                />
              ) : null}
            </Suspense>
          </motion.section>
        </AnimatePresence>
      </main>

      <TabBar activeTab={activeTab} onSelect={setActiveTab} />
    </div>
  );
};
